"use client";

import { fmtUsd } from '@/lib/finance/format'

/**
 * Shared recharts tooltip: one readout listing every series at the hovered X.
 * Values lead (strong ink), series names follow (secondary), keyed by a short
 * stroke of the series color. All text via React text nodes (never innerHTML).
 */
export function ChartTooltip({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter = fmtUsd,
  showTotal = false,
}: {
  active?: boolean
  payload?: { name?: string; value?: number | string; color?: string; dataKey?: string }[]
  label?: string | number
  labelFormatter?: (label: string) => string
  valueFormatter?: (v: number) => string
  /** Append a Total row summing the numeric series (stacked charts). */
  showTotal?: boolean
}) {
  if (!active || !payload?.length) return null
  const heading = labelFormatter ? labelFormatter(String(label)) : String(label)
  const total = payload.reduce((s, p) => s + (typeof p.value === 'number' ? p.value : 0), 0)
  return (
    <div className="pointer-events-none rounded-xl border border-edge bg-surface-1 px-3 py-2 shadow-lg">
      <div className="mb-1 text-xs font-medium text-ink-2">{heading}</div>
      <div className="space-y-0.5">
        {payload.map((p, i) => (
          <div key={p.dataKey ?? i} className="flex items-center justify-between gap-4 text-xs">
            <span className="inline-flex items-center gap-1.5 text-ink-2">
              <span
                className="inline-block h-0.5 w-3.5 rounded-full"
                style={{ background: p.color ?? 'var(--series-other)' }}
              />
              {p.name}
            </span>
            <span className="font-semibold tabular-nums text-ink">
              {typeof p.value === 'number' ? valueFormatter(p.value) : p.value}
            </span>
          </div>
        ))}
        {showTotal && (
          <div className="mt-0.5 flex items-center justify-between gap-4 border-t border-edge pt-0.5 text-xs">
            <span className="text-ink-2">Total</span>
            <span className="font-semibold tabular-nums text-ink">{valueFormatter(total)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

/** Hairline crosshair cursor for line/area charts. */
export const crosshair = { stroke: 'var(--baseline)', strokeWidth: 1 }
/** Ghost-wash cursor for bar charts. */
export const barCursor = { fill: 'var(--surface-3)', opacity: 0.6 }
